/**
 * Standard user-facing fallback messages for API errors.
 * Use with getApiErrorMessage(error, …) — never bare "Failed to …" strings.
 *
 * Upload and pagination limits must stay aligned with Aardvark.Shared:
 * - src/Aardvark.Shared/Constants/DocumentUploadConstants.cs
 * - src/Aardvark.Shared/Constants/ProfileImageUploadConstants.cs
 * - src/Aardvark.Shared/Constants/PaginationConstants.cs
 */

export const documentUpload = {
  maxFileSizeBytes: 10 * 1024 * 1024,
  maxFileSizeLabel: '10 MB',
  accept: '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv,.eml,.msg',
  allowedTypesLabel: 'PDF, Word, Excel, images, plain text, CSV, or email (.eml, .msg)',
  allowedTypesShortLabel: 'PDF, Word, Excel, images, TXT, CSV, EML, MSG',
  tooLargeMessage: 'That file is too large — the maximum size is 10 MB.',
  unsupportedTypeMessage:
    'That file type is not supported. Use PDF, Word, Excel, images, plain text, CSV, or email (.eml, .msg).',
  chooseFileMessage: 'Choose a file before continuing.',
  uploadingMessage: 'Uploading your document…',
  uploadFailedMessage: 'Could not upload your document — try again',
  helperText: 'Allowed: PDF, Word, Excel, images, TXT, CSV, EML, MSG · max 10 MB per file.',
} as const;

export const profileImageUpload = {
  maxFileSizeBytes: 10 * 1024 * 1024,
  maxFileSizeLabel: '10 MB',
  accept: 'image/*',
  allowedTypesLabel: 'PNG, JPG, GIF, or WebP',
  tooLargeMessage: 'That image is too large — the maximum size is 10 MB.',
  unsupportedTypeMessage: 'Choose a PNG, JPG, GIF, or WebP image.',
  chooseFileMessage: 'Choose an image before continuing.',
} as const;

export const pagination = {
  auditLogDefaultPageSize: 50,
  maxPageSize: 100,
  auditLogPageSizeOptions: [25, 50, 100] as const,
} as const;

export function isDocumentWithinSizeLimit(file: File): boolean {
  return file.size <= documentUpload.maxFileSizeBytes;
}

export function isProfileImageWithinSizeLimit(file: File): boolean {
  return file.size <= profileImageUpload.maxFileSizeBytes;
}

export function couldNotLoad(what: string): string {
  return `Could not load ${what} — try again`;
}

export function couldNotSave(what: string): string {
  return `Could not save ${what} — try again`;
}

export function couldNotSubmit(what: string): string {
  return `Could not submit ${what} — try again`;
}

export function couldNotUpdate(what: string): string {
  return `Could not update ${what} — try again`;
}

export function couldNotDelete(what: string): string {
  return `Could not delete ${what} — try again`;
}

export function couldNotCreate(what: string): string {
  return `Could not create ${what} — try again`;
}

export function couldNotApprove(what: string): string {
  return `Could not approve ${what} — try again`;
}

export function couldNotReject(what: string): string {
  return `Could not reject ${what} — try again`;
}

/** Review cycle deadlines shown to employees. */
export const reviewCycleCopy = {
  employeeCutoffPassed: (formattedDate: string) =>
    `The employee deadline for this review was ${formattedDate}. You can no longer complete or update your self-review.`,
  selfReviewNotSubmittedEmployee:
    'You did not complete your self-review by the closing date. It shows as incomplete and you cannot update it. An administrator can reopen it if you need more time.',
  selfReviewNotSubmittedViewer:
    'This employee did not complete their self-review by the closing date. It shows as incomplete. An administrator can reopen it if needed.',
} as const;

export function couldNotExport(what: string): string {
  return `Could not export ${what} — try again`;
}

export function couldNotConfirm(what: string): string {
  return `Could not confirm ${what} — try again`;
}

export function couldNotRecord(what: string): string {
  return `Could not record ${what} — try again`;
}

export function couldNotFinalise(what: string): string {
  return `Could not finalise ${what} — try again`;
}

export function couldNotBook(what: string): string {
  return `Could not book ${what} — try again`;
}

export function couldNotProcess(what: string): string {
  return `Could not process ${what} — try again`;
}

export function couldNotWithdraw(what: string): string {
  return `Could not withdraw ${what} — try again`;
}

export function couldNotRevise(what: string): string {
  return `Could not revise ${what} — try again`;
}

export function couldNotAdd(what: string): string {
  return `Could not add ${what} — try again`;
}

export function couldNotReopen(what: string): string {
  return `Could not reopen ${what} — try again`;
}

export function couldNotAcknowledge(what: string): string {
  return `Could not acknowledge ${what} — try again`;
}

/** Document open/download/upload/delete — same wording across leave, expenses, reviews, disputes. */
export const documentCopy = {
  open: 'Could not open your document — try again',
  download: 'Could not download your document — try again',
  delete: 'Could not delete your document — try again',
  upload: documentUpload.uploadFailedMessage,
  uploadReceipt: 'Could not upload your receipt — try again',
} as const;

/** RBAC / permission messages shown before or instead of API errors. */
export const permissionCopy = {
  administrator: "You don't have permission to view this page. Administrator access is required.",
  administratorOrSeniorManager:
    "You don't have permission to view this page. Administrator or Senior Manager access is required.",
  administratorOrManager:
    "You don't have permission to view this page. Administrator or Manager access is required.",
  allIdpsList:
    'Only administrators and senior managers can view this list. Senior managers see IDPs in their reporting hierarchy only. Use the Organisation dashboard for summary counts, or open IDPs from your Team dashboard.',
  allReviewsList:
    'Only administrators and senior managers can view this list. Senior managers see reviews in their reporting hierarchy only. Use the Organisation dashboard for summary counts, or open reviews from your Team dashboard.',
} as const;

/** Generic fallbacks when context is unclear. */
export const genericCopy = {
  somethingWrong: 'Something went wrong — try again',
  dashboard: 'Could not load your dashboard — refresh the page and try again',
  reviewCycles: 'Could not load review cycles — refresh the page and try again',
  recalculation: 'Could not recalculate — try again',
  exchangeRates: 'Could not load exchange rates — try again',
  vatOptions: 'Could not load VAT options — try again',
  governance: 'Could not load governance and compliance information — try again',
  profile: 'Could not load your profile — try again',
  referenceData: 'Could not load reference data — try again',
  userManagement: 'Could not load user management data — try again',
  sickLeaveNoteOpen: 'Could not open your sick leave note — try again',
  sickLeaveNoteDownload: 'Could not download your sick leave note — try again',
  leaveReportExport: 'Could not export the leave report — try again',
  invalidLogin: 'Invalid username or password',
  microsoftSignInFailed: 'Could not sign in with Microsoft — try again',
  peerReview: {
    load: 'Could not load your peer review — refresh the page and try again',
    submit: 'Could not submit your peer ratings — try again',
  },
  disputesAdminOnly: 'Only administrators can view and manage disputes.',
  assistantSend: 'Could not send your message — try again',
  assistantNoResponse: 'No response from the assistant — try again',
  signInOptions: 'Could not load sign-in options — check your connection and try again',
  signInRequired: 'Sign in to view email logs.',
} as const;

/** In-app Ask Aardvark (Claude connector) — starter prompts live in `data/askAardvarkPrompts.ts`. */
export const askAardvarkCopy = {
  button: 'Ask Aardvark',
  buttonTitle: 'Ask Aardvark in Claude',
  dialogTitle: 'Ask Aardvark in Claude',
  intro:
    'Once Aardvark is connected in Claude, you can ask about reviews, leave, workforce, tasks, development plans, and check-ins in everyday language. Copy a starter below, paste it into Claude, and adapt it.',
  connectHint:
    'In Claude, open Settings → Connectors, find Aardvark, and connect with your work Microsoft account. You need the same sign-in as this app.',
  readOnlyNote:
    'Ask Aardvark can only answer questions — it will not book leave, change reviews, or update plans. Use this app when you need to make changes.',
  copy: 'Copy',
  copied: 'Copied',
  copyFailed: 'Could not copy — select the text and copy manually',
  helpHeading: 'Ask Aardvark',
  close: 'Close',
  openProfileMenu: 'Open profile menu',
} as const;

export function askAardvarkHelpHint(about: string): string {
  return `You can also ask Claude about ${about} — open Ask Aardvark in the top bar for starter prompts.`;
}

/** Profile — personal on-screen timestamp time zone (calendar days stay organisation-wide). */
export const displayTimeZoneCopy = {
  heading: 'Times on screen',
  helper:
    'You can view times on screen in your preferred time zone — for example last signed in, when something was recorded, or when a file was added. Leave days, review deadlines, holidays, and emails are always shown in the organisation time zone.',
  label: 'Display times in',
  organisationOption: (iana: string) => `Organisation (${iana})`,
  useDevice: 'Use my device time zone',
  save: 'Save time zone',
  saving: 'Saving…',
  auditLogTimesHint:
    'Event times follow your preferred time zone on Profile (organisation time zone unless you chose another).',
  emailLogTimesHint:
    'Sent times follow your preferred time zone on Profile. Emails themselves stay in the organisation time zone.',
} as const;

/** Account setup soft gate — missing company, role template, etc. */
export const accountSetupCopy = {
  bannerTitle: 'Your account is not ready yet',
  bannerLead:
    'Ask HR or your administrator to complete your profile in User Management before you use reviews, leave, and other features.',
  bannerContact: 'Contact HR or your administrator if this is taking longer than expected.',
  missingCompany: 'Company (with a supported country for leave)',
  missingRoleTemplate: 'Role template (for reviews and development plans)',
  missingManager: 'Manager (recommended for check-ins and reviews)',
  missingBusinessUnit: 'Business unit (for organisation reporting)',
  missingItemLabel: (code: string): string => {
    switch (code) {
      case 'company':
        return accountSetupCopy.missingCompany;
      case 'role_template':
        return accountSetupCopy.missingRoleTemplate;
      case 'manager':
        return accountSetupCopy.missingManager;
      case 'business_unit':
        return accountSetupCopy.missingBusinessUnit;
      default:
        return code;
    }
  },
  profileCardTitle: 'Waiting for HR to finish your setup',
  profileCardBody:
    'You can view your profile and governance information here. Reviews, leave, and other features will unlock once HR assigns your company and role template.',
} as const;
